package org.gymly.data

import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.delete
import io.ktor.client.request.forms.MultiPartFormDataContent
import io.ktor.client.request.forms.formData
import io.ktor.client.request.get
import io.ktor.client.request.patch
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.Headers
import io.ktor.http.HttpHeaders
import io.ktor.http.contentType
import io.ktor.http.encodeURLParameter
import io.ktor.http.isSuccess
import org.gymly.api.ApiException
import org.gymly.api.PortalSession
import org.gymly.api.createGymlyHttpClient
import org.gymly.auth.AuthPersistence
import org.gymly.auth.SessionActivity
import org.gymly.auth.StoredAuthKind
import org.gymly.api.dto.ClientLoginRequestDto
import org.gymly.api.dto.ClientLoginResponseDto
import org.gymly.api.dto.MarketingPromotionDto
import org.gymly.api.dto.PortalCreateBookingDto
import org.gymly.api.dto.PortalHomeDto
import org.gymly.api.dto.PortalMeDto
import org.gymly.api.dto.PortalPasswordBodyDto
import org.gymly.api.dto.PortalPaymentDto
import org.gymly.api.dto.PortalUpdateProfileDto
import org.gymly.api.resolveApiUrl
import org.gymly.util.portalApiPrefix
import org.gymly.util.portalReturnUrl
import org.gymly.util.portalLoginPath
import org.gymly.util.toPortalChannelPath
import org.gymly.model.ClientPaymentMethod
import org.gymly.model.MarketingItem
import org.gymly.model.MarketingSlide
import org.gymly.model.PortalBooking
import org.gymly.model.PortalHome
import org.gymly.model.PortalMe
import org.gymly.model.PortalPayment
import org.gymly.model.PortalProfile
import org.gymly.model.PortalSchedule
import org.gymly.model.PortalStoreOrder
import org.gymly.model.PortalStoreOrderItem
import org.gymly.model.PortalStoreOrderStatus
import org.gymly.model.PortalStorePayMethod
import org.gymly.model.PortalStoreCategory
import org.gymly.model.PortalStoreProduct
import org.gymly.model.PortalStoreProductsPage
import org.gymly.model.PortalUser
class HttpPortalRepository(
    private val session: PortalSession,
    val client: HttpClient = createGymlyHttpClient(session),
) : PortalRepository {

    private fun url(path: String): String = resolveApiUrl(session, path)

    private suspend inline fun <reified T> get(path: String): T {
        val response = client.get(url(path))
        if (!response.status.isSuccess()) {
            throw portalApiException(response.status.value, response.bodyAsText().take(2000))
        }
        return response.body()
    }

    private suspend inline fun <reified T> post(path: String, body: Any? = null): T {
        val response = client.post(url(path)) {
            contentType(ContentType.Application.Json)
            if (body != null) setBody(body)
        }
        if (!response.status.isSuccess()) {
            throw portalApiException(response.status.value, response.bodyAsText().take(2000))
        }
        return response.body()
    }

    private suspend inline fun <reified T> put(path: String, body: Any): T {
        val response = client.put(url(path)) {
            contentType(ContentType.Application.Json)
            setBody(body)
        }
        if (!response.status.isSuccess()) {
            throw portalApiException(response.status.value, response.bodyAsText().take(2000))
        }
        return response.body()
    }

    private suspend fun patchNoContent(path: String, body: Any) {
        val response = client.patch(url(path)) {
            contentType(ContentType.Application.Json)
            setBody(body)
        }
        if (!response.status.isSuccess()) {
            throw portalApiException(response.status.value, response.bodyAsText().take(2000))
        }
    }

    private suspend fun deleteNoContent(path: String) {
        val response = client.delete(url(path))
        if (!response.status.isSuccess()) {
            throw portalApiException(response.status.value, response.bodyAsText().take(2000))
        }
    }

    private suspend fun postNoContent(path: String) {
        val response = client.post(url(path))
        if (!response.status.isSuccess()) {
            throw portalApiException(response.status.value, response.bodyAsText().take(2000))
        }
    }

    override suspend fun startStoreCheckout(orderId: String): String =
        post<org.gymly.api.dto.PortalStoreCheckoutLinkDto>(
            "$portalApiPrefix/store/orders/$orderId/checkout",
            org.gymly.api.dto.PortalStoreCheckoutLinkRequestDto(
                returnUrl = portalReturnUrl(org.gymly.model.PortalTab.Store, session.apiBase),
            ),
        ).url

    override suspend fun startMembershipCheckout(
        planId: String,
        trainerId: String,
        method: org.gymly.model.ClientPaymentMethod,
        notes: String?,
    ): org.gymly.model.PortalMembershipCheckout =
        post<org.gymly.api.dto.PortalMembershipCheckoutDto>(
            "$portalApiPrefix/membership-orders/pagos/pasarela",
            org.gymly.api.dto.PortalMembershipCheckoutRequestDto(
                planId = planId,
                trainerId = trainerId,
                method = method.name,
                notes = notes?.trim()?.takeIf { it.isNotEmpty() },
                returnUrl = portalReturnUrl(org.gymly.model.PortalTab.Payments, session.apiBase),
            ),
        ).let { org.gymly.model.PortalMembershipCheckout(it.paymentId, it.url) }

    override suspend fun confirmMembershipCheckout(paymentId: String): Boolean =
        get<org.gymly.api.dto.PortalMembershipCheckoutStatusDto>(
            "$portalApiPrefix/membership-orders/pagos/$paymentId/pasarela",
        ).paid

    override suspend fun fetchTerms(): org.gymly.model.PortalTerms =
        get<org.gymly.api.dto.PortalTermsDto>("$portalApiPrefix/terms").toTerms()

    override suspend fun acceptTerms(version: String, marketingWhatsApp: Boolean): org.gymly.model.PortalTerms =
        post<org.gymly.api.dto.PortalTermsDto>(
            "$portalApiPrefix/terms/accept",
            org.gymly.api.dto.PortalAcceptTermsRequestDto(version = version, marketingWhatsApp = marketingWhatsApp),
        ).toTerms()

    override suspend fun declineTerms(version: String): org.gymly.model.PortalTerms =
        post<org.gymly.api.dto.PortalTermsDto>(
            "$portalApiPrefix/terms/decline",
            org.gymly.api.dto.PortalDeclineTermsRequestDto(version = version),
        ).toTerms()

    private fun org.gymly.api.dto.PortalTermsDto.toTerms() = org.gymly.model.PortalTerms(
        version = version,
        title = title,
        body = body,
        accepted = accepted,
        marketingWhatsApp = marketingWhatsApp,
        suspended = suspended,
        suspensionReason = suspensionReason,
        suspensionNote = suspensionNote,
        required = required,
    )

    override suspend fun confirmStoreCheckout(orderId: String): PortalStoreOrder =
        get<org.gymly.api.dto.PortalStoreOrderDto>("$portalApiPrefix/store/orders/$orderId/checkout")
            .toStoreOrder(session)

    override suspend fun passwordRecoveryAvailable(): Boolean =
        get<org.gymly.api.dto.PasswordRecoveryStatusDto>("/api/auth/password-recovery/status").available

    override suspend fun requestPasswordRecovery(scope: String, email: String) {
        post<org.gymly.api.dto.PasswordRecoveryMessageDto>(
            "/api/auth/password-recovery/request",
            org.gymly.api.dto.PasswordRecoveryRequestDto(scope = scope, email = email.trim()),
        )
    }

    override suspend fun confirmPasswordRecovery(
        scope: String,
        email: String,
        code: String,
        newPassword: String,
    ): String = post<org.gymly.api.dto.PasswordRecoveryMessageDto>(
        "/api/auth/password-recovery/confirm",
        org.gymly.api.dto.PasswordRecoveryConfirmDto(
            scope = scope,
            email = email.trim(),
            code = code.trim(),
            newPassword = newPassword,
        ),
    ).message

    override suspend fun login(email: String, password: String): PortalUser {
        if (email.isBlank() || password.isBlank()) {
            error("Completa correo y contraseña.")
        }
        session.accessToken = null
        val res: ClientLoginResponseDto = post(
            portalLoginPath,
            ClientLoginRequestDto(email = email.trim(), password = password),
        )
        session.accessToken = res.accessToken
        AuthPersistence.save(StoredAuthKind.CLIENT, res.accessToken)
        SessionActivity.markSessionStarted()
        return PortalUser(
            id = res.user.id,
            displayName = res.user.displayName,
            email = res.user.email,
            role = res.user.role,
            preferredLocale = res.user.preferredLocale,
        )
    }

    override suspend fun fetchSessionUser(): PortalUser =
        get<PortalMeDto>("$portalApiPrefix/me").toSessionUser()

    override suspend fun fetchHome(): PortalHome = get<PortalHomeDto>("$portalApiPrefix/home").toDomain(session)

    override suspend fun fetchMarketing(): List<MarketingItem> =
        get<List<MarketingPromotionDto>>("/api/marketing/promotions?forMobile=true").map { it.toDomain(session) }

    override suspend fun fetchMe(): PortalMe = get<PortalMeDto>("$portalApiPrefix/me").toDomain(session)

    override suspend fun updateProfile(
        firstName: String,
        lastName: String,
        email: String?,
        phone: String?,
        preferredLocale: String?,
    ): PortalMe =
        put<PortalMeDto>(
            "$portalApiPrefix/me",
            PortalUpdateProfileDto(
                firstName = firstName.trim(),
                lastName = lastName.trim(),
                email = email?.trim()?.takeIf { it.isNotEmpty() },
                phone = phone?.trim()?.takeIf { it.isNotEmpty() },
                photoUrl = null,
                preferredLocale = preferredLocale?.trim()?.takeIf { it.isNotEmpty() },
            ),
        ).toDomain(session)

    override suspend fun uploadPhoto(bytes: ByteArray, fileName: String, mimeType: String): PortalMe {
        val response = client.post(url("$portalApiPrefix/me/photo")) {
            setBody(
                MultiPartFormDataContent(
                    formData {
                        appendUploadFile(
                            "file",
                            org.gymly.model.PickedImagePayload(bytes, fileName, mimeType),
                        )
                    },
                ),
            )
        }
        if (!response.status.isSuccess()) {
            throw portalApiException(response.status.value, response.bodyAsText().take(2000))
        }
        return response.body<PortalMeDto>().toDomain(session)
    }

    override suspend fun deletePhoto() = deleteNoContent("$portalApiPrefix/me/photo")

    override suspend fun changePassword(current: String, newPassword: String) {
        patchNoContent("$portalApiPrefix/me/password", PortalPasswordBodyDto(current, newPassword))
    }

    override suspend fun fetchPayments(): List<PortalPayment> =
        get<List<PortalPaymentDto>>("$portalApiPrefix/payments").map { it.toDomain(session) }

    override suspend fun checkPaymentReference(reference: String, dateIso: String): Boolean =
        get<org.gymly.api.dto.PortalReferenceCheckDto>(
            "$portalApiPrefix/payment-references/check?reference=${reference.trim().encodeURLQuery()}&date=${dateIso.take(10)}",
        ).duplicate

    override suspend fun uploadPaymentProof(id: String, image: org.gymly.model.PickedImagePayload): PortalPayment {
        val response = client.post(url("$portalApiPrefix/payments/$id/proof")) {
            setBody(
                MultiPartFormDataContent(
                    formData { appendUploadFile("file", image) },
                ),
            )
        }
        if (!response.status.isSuccess()) {
            throw portalApiException(response.status.value, response.bodyAsText().take(2000))
        }
        return response.body<PortalPaymentDto>().toDomain(session)
    }

    override suspend fun deletePayment(id: String) = deleteNoContent("$portalApiPrefix/payments/$id")

    override suspend fun fetchMembershipOrders(): List<org.gymly.model.PortalMembershipOrder> =
        get<List<org.gymly.api.dto.PortalMembershipOrderDto>>("$portalApiPrefix/membership-orders").map { it.toDomain() }

    override suspend fun fetchMembershipOptions(): org.gymly.model.PortalMembershipOptions =
        get<org.gymly.api.dto.PortalMembershipOptionsDto>("$portalApiPrefix/membership-orders/opciones").let { dto ->
            org.gymly.model.PortalMembershipOptions(
                plans = dto.planes.map {
                    org.gymly.model.PortalPlanOption(it.id, it.nombre, it.priceCents, it.priceUsdCents)
                },
                trainers = dto.entrenadores.map { org.gymly.model.PortalTrainerOption(it.id, it.nombre) },
            )
        }

    override suspend fun createMembershipPayment(
        planId: String,
        trainerId: String,
        method: ClientPaymentMethod,
        notes: String?,
        paymentDate: String?,
    ): org.gymly.model.PortalMembershipOrder =
        post<org.gymly.api.dto.PortalMembershipOrderDto>(
            "$portalApiPrefix/membership-orders/pagos",
            org.gymly.api.dto.PortalCreateMembershipPaymentDto(
                planId = planId,
                trainerId = trainerId,
                method = method.name,
                notes = notes?.trim()?.takeIf { it.isNotEmpty() },
                paymentDate = paymentDate?.trim()?.takeIf { it.isNotEmpty() },
            ),
        ).toDomain()

    override suspend fun fetchBookings(): List<PortalBooking> =
        get<List<org.gymly.api.dto.PortalBookingDto>>("$portalApiPrefix/bookings").map { it.toDomain() }

    override suspend fun fetchPaymentProofRules(): Map<String, Boolean> =
        get<Map<String, Boolean>>("$portalApiPrefix/payments/methods")

    override suspend fun fetchSchedulesWeek(weekStart: String?): List<PortalSchedule> {
        val path = if (weekStart == null) "$portalApiPrefix/schedules" else "$portalApiPrefix/schedules?weekStart=$weekStart"
        return get<List<org.gymly.api.dto.PortalScheduleDto>>(path).map { it.toDomain() }
    }

    override suspend fun createBooking(scheduleId: String, sessionDate: String?, notes: String?) {
        post<org.gymly.api.dto.PortalBookingDto>(
            "$portalApiPrefix/bookings",
            PortalCreateBookingDto(scheduleId, sessionDate, notes?.trim()?.takeIf { it.isNotEmpty() }),
        )
    }

    override suspend fun cancelBooking(id: String) = postNoContent("$portalApiPrefix/bookings/$id/cancel")

    override suspend fun fetchStoreProducts(): List<PortalStoreProduct> =
        get<List<org.gymly.api.dto.PortalStoreProductDto>>("$portalApiPrefix/store/products").map { it.toStoreProduct(session) }

    override suspend fun searchStoreProducts(query: String, categoryId: String?, page: Int, size: Int): PortalStoreProductsPage {
        val params = buildString {
            append("?q=").append(query.encodeURLParameter())
            if (categoryId != null) append("&categoryId=").append(categoryId.encodeURLParameter())
            append("&page=").append(page).append("&size=").append(size)
        }
        val dto = get<org.gymly.api.dto.PortalStoreProductsPageDto>("$portalApiPrefix/store/products/search$params")
        return PortalStoreProductsPage(
            items = dto.items.map { it.toStoreProduct(session) },
            page = dto.page,
            hasMore = dto.hasMore,
            categories = dto.categories.map { PortalStoreCategory(it.id, it.name) },
            totalCount = dto.totalCount,
        )
    }

    override suspend fun fetchStoreOrders(): List<PortalStoreOrder> =
        get<List<org.gymly.api.dto.PortalStoreOrderDto>>("$portalApiPrefix/store/orders").map { it.toStoreOrder(session) }

    override suspend fun checkoutStore(
        method: PortalStorePayMethod,
        items: List<Pair<String, Int>>,
        reference: String,
        paymentDate: String?,
    ): PortalStoreOrder =
        post<org.gymly.api.dto.PortalStoreOrderDto>(
            "$portalApiPrefix/store/orders",
            org.gymly.api.dto.PortalStoreCheckoutRequestDto(
                method = method.name,
                items = items.map { (id, qty) ->
                    org.gymly.api.dto.PortalStoreCheckoutItemDto(productId = id, qty = qty)
                },
                reference = reference,
                paymentDate = paymentDate?.takeIf { it.isNotBlank() },
            ),
        ).toStoreOrder(session)

    override suspend fun uploadStoreOrderProof(id: String, image: org.gymly.model.PickedImagePayload): PortalStoreOrder {
        val response = client.post(url("$portalApiPrefix/store/orders/$id/proof")) {
            setBody(
                MultiPartFormDataContent(
                    formData { appendUploadFile("file", image) },
                ),
            )
        }
        if (!response.status.isSuccess()) {
            throw portalApiException(response.status.value, response.bodyAsText().take(2000))
        }
        return response.body<org.gymly.api.dto.PortalStoreOrderDto>().toStoreOrder(session)
    }

    override suspend fun fetchClientNotifications(): List<org.gymly.model.PortalClientNotification> =
        get<List<org.gymly.api.dto.PortalClientNotificationDto>>("$portalApiPrefix/notifications")
            .map { it.toClientNotification() }

    override suspend fun fetchUnreadNotificationCount(): Long =
        get<org.gymly.api.dto.PortalUnreadCountDto>("$portalApiPrefix/notifications/unread-count").unreadCount

    override suspend fun markClientNotificationRead(id: String) =
        postNoContent("$portalApiPrefix/notifications/$id/read")

    override suspend fun fetchNotificationPreferences(): org.gymly.model.PortalNotificationPreferences =
        get<org.gymly.api.dto.PortalNotificationPreferencesDto>("$portalApiPrefix/notification-preferences")
            .toPrefs()

    override suspend fun saveNotificationPreferences(
        prefs: org.gymly.model.PortalNotificationPreferences,
    ): org.gymly.model.PortalNotificationPreferences =
        put<org.gymly.api.dto.PortalNotificationPreferencesDto>(
            "$portalApiPrefix/notification-preferences",
            org.gymly.api.dto.PortalNotificationPreferencesDto(
                paymentReminders = prefs.paymentReminders,
                classReminders = prefs.classReminders,
                promotions = prefs.promotions,
                staffMessages = prefs.staffMessages,
            ),
        ).toPrefs()

    override fun logout() {
        session.clearAuth()
        AuthPersistence.clear(StoredAuthKind.CLIENT)
        SessionActivity.clear()
    }
}

private fun PortalMeDto.toSessionUser() = PortalUser(
    id = id,
    displayName = displayName ?: listOf(firstName, lastName).joinToString(" ").trim(),
    email = email,
    role = "CLIENT",
    preferredLocale = preferredLocale,
)

private fun PortalHomeDto.toDomain(session: PortalSession) = PortalHome(
    profile = profile.toProfile(session),
    recentPayments = recentPayments.map { it.toDomain(session) },
    upcomingClasses = upcomingClasses.map { it.toDomain() },
    weekSchedules = weekSchedules.map { it.toDomain() },
    schedulesAutomatic = schedulesAutomatic,
    today = today,
    recentStoreOrders = recentStoreOrders.map { it.toStoreOrder(session) },
)

private fun resolveMediaUrl(session: PortalSession?, path: String?): String? {
    if (path.isNullOrBlank()) return null
    if (path.startsWith("http://") || path.startsWith("https://") || path.startsWith("data:")) return path
    if (session == null) return path
    return resolveApiUrl(session, toPortalChannelPath(path))
}

private fun PortalMeDto.toProfile(session: PortalSession?) = PortalProfile(
    firstName = firstName,
    lastName = lastName,
    email = email,
    phone = phone,
    photoUrl = resolveMediaUrl(session, photoUrl),
    displayName = displayName ?: listOf(firstName, lastName).joinToString(" ").trim(),
    branchName = branchName,
    statusLabel = statusLabel,
)

private fun PortalMeDto.toDomain(session: PortalSession) = PortalMe(
    firstName = firstName,
    lastName = lastName,
    email = email,
    phone = phone,
    photoUrl = resolveMediaUrl(session, photoUrl),
    preferredLocale = preferredLocale,
    displayName = displayName ?: listOf(firstName, lastName).joinToString(" ").trim(),
    branchName = branchName,
    statusLabel = statusLabel,
    mustChangePassword = mustChangePassword,
)

private fun PortalPaymentDto.toDomain(session: PortalSession): PortalPayment {
    val methodEnum = org.gymly.model.ClientPaymentMethod.fromApi(method)
    val statusEnum = org.gymly.model.PortalPaymentStatus.fromApi(status)
    return PortalPayment(
        id = id,
        amountCents = amountCents.toInt(),
        currency = currency,
        method = methodEnum,
        methodLabel = methodLabel ?: methodEnum.labelEs(),
        status = statusEnum,
        statusLabel = statusLabel ?: statusEnum.labelEs(),
        notes = notes,
        paymentDate = paymentDate?.takeIf { it.isNotBlank() }
            ?: paidAt?.take(10).orEmpty(),
        canEdit = canEdit,
        proofUrl = resolveMediaUrl(session, proofUrl),
        proofContentType = proofContentType,
    )
}

private fun org.gymly.api.dto.PortalBookingDto.toDomain(): PortalBooking {
    val st = org.gymly.model.PortalBookingStatus.fromApi(status)
    return PortalBooking(
        id = id,
        title = title,
        sessionDate = sessionDate,
        weekday = weekday,
        startTime = startTime,
        endTime = endTime,
        status = st,
        statusLabel = statusLabel ?: st.labelEs(),
        canCancel = canCancel,
    )
}

private fun org.gymly.api.dto.PortalScheduleDto.toDomain(): PortalSchedule {
    val shiftEnum = org.gymly.model.PortalScheduleShift.fromApi(shift)
    return PortalSchedule(
        id = id,
        title = title,
        weekday = weekday,
        startTime = startTime,
        endTime = endTime,
        trainerName = trainerName,
        shift = shiftEnum,
        shiftLabel = shiftLabel ?: shiftEnum.labelEs(),
        capacity = capacity,
        bookedCount = bookedCount,
        availableSpots = availableSpots,
        full = full,
        nextSessionDate = nextSessionDate,
        alreadyBooked = alreadyBooked,
        bookable = bookable,
    )
}

private fun MarketingPromotionDto.toDomain(session: PortalSession) = MarketingItem(
    id = id,
    title = title,
    details = details,
    displayType = displayType,
    slides = slides.map {
        MarketingSlide(
            id = it.id,
            fullUrl = resolveApiUrl(session, it.fullUrl),
            thumbUrl = resolveApiUrl(session, it.thumbUrl),
            sortIndex = it.sortIndex,
        )
    },
    videoUrl = videoUrl?.let { resolveApiUrl(session, it) },
)

private fun org.gymly.api.dto.PortalStoreProductDto.toStoreProduct(session: PortalSession) = PortalStoreProduct(
    id = id,
    name = name,
    category = category,
    categoryLabel = categoryLabel ?: category,
    priceCents = priceCents,
    priceUsdCents = priceUsdCents,
    currency = currency,
    stockQty = stockQty,
    description = description,
    ingredients = ingredients,
    videoUrl = videoUrl,
    imageUrl = imageUrl?.let { resolveApiUrl(session, it) },
)

/** Error de la API del portal. Si el servidor bloquea por términos o suspensión, el gate vuelve a revisarlos. */
private fun portalApiException(status: Int, body: String): ApiException {
    if (body.contains("TERMS_NOT_ACCEPTED") || body.contains("CLIENT_SUSPENDED") || body.contains("TERMS_CHECK_FAILED")) {
        PortalTermsGuard.requestRecheck()
    }
    return ApiException(status, body)
}

private fun org.gymly.api.dto.PortalStoreOrderDto.toStoreOrder(session: PortalSession) = PortalStoreOrder(
    id = id,
    saleNumber = saleNumber,
    totalCents = totalCents,
    currency = currency,
    method = method,
    methodLabel = methodLabel ?: method,
    status = PortalStoreOrderStatus.fromApi(status),
    statusLabel = statusLabel ?: PortalStoreOrderStatus.fromApi(status).badgeLabel(),
    saleDate = saleDate.ifBlank { createdAt.orEmpty() },
    itemsSummary = itemsSummary.orEmpty().ifBlank {
        items.joinToString(", ") { "${it.productName} x${it.qty}" }
    },
    items = items.map {
        PortalStoreOrderItem(
            productId = it.productId,
            productName = it.productName,
            qty = it.qty,
            unitPriceCents = it.unitPriceCents,
            lineTotalCents = it.lineTotalCents,
        )
    },
    proofUrl = resolveMediaUrl(session, proofUrl),
    proofContentType = proofContentType,
)

private fun org.gymly.api.dto.PortalClientNotificationDto.toClientNotification(): org.gymly.model.PortalClientNotification {
    val cat = org.gymly.model.PortalNotificationCategory.fromApi(category)
    return org.gymly.model.PortalClientNotification(
        id = id,
        category = cat,
        categoryLabel = categoryLabel ?: cat.preferenceLabel(),
        title = title,
        body = body,
        read = read,
        relativeLabel = relativeLabel.orEmpty(),
    )
}

private fun org.gymly.api.dto.PortalNotificationPreferencesDto.toPrefs() =
    org.gymly.model.PortalNotificationPreferences(
        paymentReminders = paymentReminders,
        classReminders = classReminders,
        promotions = promotions,
        staffMessages = staffMessages,
    )

private fun org.gymly.api.dto.PortalMembershipOrderDto.toDomain() = org.gymly.model.PortalMembershipOrder(
    id = id,
    orderNumber = orderNumber,
    planName = planName,
    trainerName = trainerName,
    orderDate = orderDate,
    amountCents = amountCents,
    currency = currency,
    status = status,
    statusLabel = statusLabel,
    endsAt = endsAt,
    payments = payments,
    paymentId = paymentId,
)
